import React, { useState, useEffect } from 'react';
import { PiggyBank } from 'lucide-react';
import { SEO } from '../components/SEO';

interface SavingsState {
  initialAmount: string;
  monthlyContribution: string;
  years: string;
  interestRate: string;
  currency: 'EUR' | 'BAM' | 'RSD';
  compoundingFrequency: 'monthly' | 'quarterly' | 'annually';
}

const currencySymbols: Record<string, string> = {
  EUR: '€',
  BAM: 'KM',
  RSD: 'RSD'
};

function KalkulatorStednje() {
  const [values, setValues] = useState<SavingsState>({
    initialAmount: '',
    monthlyContribution: '',
    years: '',
    interestRate: '',
    currency: 'EUR',
    compoundingFrequency: 'monthly'
  });

  const [results, setResults] = useState<{
    finalBalance: number;
    totalContributions: number;
    totalInterest: number;
    yearlyBreakdown: {
      year: number;
      balance: number;
      contributions: number;
      interest: number;
    }[];
  } | null>(null);

  const calculateSavings = () => {
    const initial = parseFloat(values.initialAmount) || 0;
    const monthly = parseFloat(values.monthlyContribution) || 0;
    const years = parseFloat(values.years) || 0;
    const annualRate = (parseFloat(values.interestRate) || 0) / 100;

    if (years <= 0) return null;

    let periodsPerYear: number;
    switch (values.compoundingFrequency) {
      case 'monthly': periodsPerYear = 12; break;
      case 'quarterly': periodsPerYear = 4; break;
      case 'annually': periodsPerYear = 1; break;
      default: periodsPerYear = 12;
    }

    const periodicRate = annualRate / periodsPerYear;
    const totalPeriods = years * periodsPerYear;
    const yearlyBreakdown = [];
    
    let balance = initial;
    let totalContributions = initial;
    let totalInterest = 0;

    for (let year = 1; year <= years; year++) {
      let yearlyContributions = 0;
      let yearlyInterest = 0;

      for (let period = 1; period <= periodsPerYear; period++) {
        const contribution = monthly * (12 / periodsPerYear);
        yearlyContributions += contribution;
        balance += contribution;
        
        const periodInterest = balance * periodicRate;
        balance += periodInterest;
        yearlyInterest += periodInterest;
      }

      totalContributions += yearlyContributions;
      totalInterest += yearlyInterest;

      yearlyBreakdown.push({
        year,
        balance,
        contributions: yearlyContributions,
        interest: yearlyInterest
      });
    }

    return {
      finalBalance: parseFloat(balance.toFixed(2)),
      totalContributions: parseFloat(totalContributions.toFixed(2)),
      totalInterest: parseFloat(totalInterest.toFixed(2)),
      yearlyBreakdown
    };
  };

  const handleInputChange = (field: keyof SavingsState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.initialAmount && values.years && values.interestRate) {
      const results = calculateSavings();
      setResults(results);
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Štednje",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte buduću vrijednost vaše štednje. Uključuje složeni kamatni račun i mjesečne uplate za dugoročno planiranje štednje.",
    "url": "https://online-kalkulator.com/kalkulator-stednje",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Štednje | Izračun Štednje i Kamata"
        description="Izračunajte buduću vrijednost vaše štednje. Uključuje složeni kamatni račun i mjesečne uplate za dugoročno planiranje štednje."
        canonicalUrl="/kalkulator-stednje"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <PiggyBank className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Štednje</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Početni iznos
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.initialAmount}
                      onChange={(e) => handleInputChange('initialAmount', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite iznos u ${values.currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {currencySymbols[values.currency]}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mjesečna uplata
                  </label>
                  <input
                    type="number"
                    value={values.monthlyContribution}
                    onChange={(e) => handleInputChange('monthlyContribution', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mjesečni iznos"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valuta
                  </label>
                  <select
                    value={values.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value as SavingsState['currency'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="EUR">Euro (€)</option>
                    <option value="BAM">Konvertibilna marka (KM)</option>
                    <option value="RSD">Srpski dinar (RSD)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Godišnja kamatna stopa (%)
                  </label>
                  <input
                    type="number"
                    value={values.interestRate}
                    onChange={(e) => handleInputChange('interestRate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 3.5"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Razdoblje štednje (godine)
                  </label>
                  <input
                    type="number"
                    value={values.years}
                    onChange={(e) => handleInputChange('years', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Obračun kamata
                  </label>
                  <select
                    value={values.compoundingFrequency}
                    onChange={(e) => handleInputChange('compoundingFrequency', e.target.value as SavingsState['compoundingFrequency'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="monthly">Mjesečno</option>
                    <option value="quarterly">Kvartalno</option>
                    <option value="annually">Godišnje</option>
                  </select>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupna štednja</div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatCurrency(results.finalBalance)} {currencySymbols[values.currency]}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupne uplate</div>
                      <div className="text-xl font-bold text-green-600">
                        {formatCurrency(results.totalContributions)} {currencySymbols[values.currency]}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupna kamata</div>
                      <div className="text-xl font-bold text-purple-600">
                        {formatCurrency(results.totalInterest)} {currencySymbols[values.currency]}
                      </div>
                    </div>
                  </div>

                  {/* Yearly Breakdown */}
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Godišnji pregled</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Godina
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Uplate
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Kamata
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Stanje
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {results.yearlyBreakdown.map((year) => (
                            <tr key={year.year}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {year.year}.
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(year.contributions)} {currencySymbols[values.currency]}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(year.interest)} {currencySymbols[values.currency]}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatCurrency(year.balance)} {currencySymbols[values.currency]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Štednje</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator štednje je koristan alat koji vam pomaže planirati i vizualizirati rast
                  vaše štednje tijekom vremena. Uzima u obzir početni ulog, redovite mjesečne uplate
                  i složeni kamatni račun za precizan izračun potencijalnog prinosa.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Radi Složeni Kamatni Račun</h2>
                <p className="text-gray-600 mb-4">
                  Složeni kamatni račun je proces u kojem se kamata obračunava ne samo na početni ulog,
                  već i na prethodno zarađenu kamatu. Ovo stvara efekt "kamata na kamatu" koji može
                  značajno povećati vašu štednju tijekom vremena.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Mjesečni obračun: Kamata se obračunava 12 puta godišnje</li>
                  <li>Kvartalni obračun: Kamata se obračunava 4 puta godišnje</li>
                  <li>Godišnji obračun: Kamata se obračunava jednom godišnje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite početni iznos koji želite uložiti</li>
                  <li>Odredite mjesečni iznos koji ćete dodatno uplaćivati</li>
                  <li>Postavite razdoblje štednje u godinama</li>
                  <li>Unesite godišnju kamatnu stopu</li>
                  <li>Odaberite učestalost obračuna kamata</li>
                  <li>Pregledajte rezultate i godišnji pregled</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su informativne prirode</li>
                  <li>Ne uključuju inflaciju i porezne obveze</li>
                  <li>Pretpostavljaju konstantnu kamatnu stopu</li>
                  <li>Stvarni prinosi mogu odstupati od izračuna</li>
                  <li>Konzultirajte financijskog savjetnika za osobni plan</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Uspješnu Štednju</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Počnite štedjeti što ranije zbog efekta složenog kamatnog računa</li>
                  <li>Redovito uplaćujte, čak i male iznose</li>
                  <li>Postavite realne ciljeve štednje</li>
                  <li>Usporedite različite opcije štednje i kamatne stope</li>
                  <li>Razmislite o diverzifikaciji štednje</li>
                  <li>Izbjegavajte prijevremeno podizanje sredstava</li>
                  <li>Redovito pratite i prilagođavajte plan štednje</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorStednje;